import type { CoverageGapCell, CoverageMatrixRow, CoverageReport } from "../../api/types.js";
import {
  handleInternalNavigation,
  pendingFindingsHref,
  safeConsoleHref,
} from "../../app/routing.js";
import { formatDateTime, formatInteger, formatPercent } from "../../lib/format.js";

const HEATMAP_FAMILIES = ["sast", "secrets", "sca", "iac", "container", "pqc"] as const;

export function coverageStatusLabel(status: CoverageMatrixRow["status"]): string {
  if (status === "partial") return "mixed, not reviewed";
  return status.replaceAll("_", " ");
}

const FAMILY_LABELS: Record<(typeof HEATMAP_FAMILIES)[number], string> = {
  sast: "SAST",
  secrets: "Secrets",
  sca: "SCA",
  iac: "IaC",
  container: "Container",
  pqc: "PQC",
};

export function trustFromStatus(status: CoverageMatrixRow["status"] | "unavailable"): {
  kind: "trusted" | "not-trusted" | "unknown" | "out-of-scope";
  verdict: string;
  why: string;
} {
  if (status === "reviewed") {
    return {
      kind: "trusted",
      verdict: "Trusted",
      why: "Reviewed with a recorded denominator. This family can support a decision.",
    };
  }
  if (status === "partial") {
    return {
      kind: "not-trusted",
      verdict: "Do not trust",
      why: "Mixed evidence. Some files were reviewed; others were not.",
    };
  }
  if (status === "blocked" || status === "failed" || status === "stale") {
    return {
      kind: "not-trusted",
      verdict: "Do not trust",
      why:
        status === "blocked"
          ? "This family was blocked. Zero evidenced files is not a clean result."
          : status === "failed"
            ? "Review failed. Findings from this family are incomplete."
            : "Evidence is stale relative to the frozen snapshot.",
    };
  }
  if (status === "unsupported" || status === "not_applicable" || status === "excluded") {
    return {
      kind: "out-of-scope",
      verdict: "Not in scope",
      why: "This family does not apply to these files, or no analyzer ran. That is not a finding queue.",
    };
  }
  return {
    kind: "unknown",
    verdict: "Unknown",
    why: "Review has not started. An empty finding list here is not evidence of safety.",
  };
}

export function groupUnreviewedCells(cells: readonly CoverageGapCell[] | undefined): Array<{
  key: string;
  family: string;
  state: string;
  reason: string;
  count: number;
  href: string;
}> {
  const groups = new Map<
    string,
    { family: string; state: string; reason: string; count: number; href: string }
  >();
  for (const cell of cells ?? []) {
    const key = `${cell.family}:${cell.state}:${cell.reason}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    groups.set(key, {
      family: cell.family,
      state: cell.state,
      reason: cell.reason,
      count: 1,
      href: cell.href,
    });
  }
  return [...groups.entries()]
    .map(([key, group]) => ({ key, ...group }))
    .sort((left, right) => right.count - left.count);
}

function coverageCardMeaning(item: CoverageReport["items"][number], ratio: number): string {
  const label = item.label.toLocaleLowerCase();
  if (label.includes("complete scan") && ratio === 0) {
    return "This does not mean nothing ran. It means no repository has every required evidence family fully reviewed yet.";
  }
  if (label.includes("reviewed")) {
    return "Reviewed is the only state that can look clean; every other state remains visible as unfinished evidence.";
  }
  if (label.includes("finding") || label.includes("canonical")) {
    return "These are the issues that remain tied to replayable source evidence after normalization.";
  }
  return "Open the linked evidence before treating this number as a decision.";
}

function coverageCardValue(item: CoverageReport["items"][number], ratio: number): string {
  const label = item.label.toLocaleLowerCase();
  if (label.includes("complete scan") && ratio === 0) return "Gaps visible";
  return formatPercent(ratio);
}

function coverageCardCount(item: CoverageReport["items"][number], ratio: number): string {
  const label = item.label.toLocaleLowerCase();
  if (label.includes("complete scan") && ratio === 0) {
    return `${formatInteger(item.total)} scans checked for full-family evidence`;
  }
  return `${formatInteger(item.covered)} of ${formatInteger(item.total)} ${item.unitLabel}`;
}

export function repositoryFamilyHeatmap(
  matrix: readonly CoverageMatrixRow[],
  options?: { cartesian?: boolean },
): {
  repositories: Array<{ id: string; label: string }>;
  families: readonly string[];
  cell: (repositoryId: string, family: string) => CoverageMatrixRow | null;
} {
  const repositories: Array<{ id: string; label: string }> = [];
  const seen = new Set<string>();
  for (const row of matrix) {
    if (seen.has(row.repositoryId)) continue;
    seen.add(row.repositoryId);
    repositories.push({ id: row.repositoryId, label: row.repositoryLabel });
  }
  const present = new Set(matrix.map((row) => row.family));
  const families = options?.cartesian
    ? HEATMAP_FAMILIES
    : HEATMAP_FAMILIES.filter((family) => present.has(family));
  return {
    repositories,
    families,
    cell: (repositoryId, family) =>
      matrix.find((row) => row.repositoryId === repositoryId && row.family === family) ?? null,
  };
}

function CoverageFamilyHeatmap({
  matrix,
  cartesian,
  navigate,
}: {
  matrix: readonly CoverageMatrixRow[];
  cartesian: boolean;
  navigate: (path: string) => void;
}) {
  const heatmap = repositoryFamilyHeatmap(matrix, { cartesian });
  if (heatmap.repositories.length === 0 || heatmap.families.length === 0) return null;
  return (
    <table
      className="coverage-family-heatmap evidence-issue-table"
      aria-label="Repository by analysis-family coverage heatmap"
    >
      <thead>
        <tr>
          <th>Repository</th>
          {heatmap.families.map((family) => (
            <th key={family}>{family.toUpperCase()}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {heatmap.repositories.map((repository) => (
          <tr key={repository.id}>
            <th scope="row">{repository.label}</th>
            {heatmap.families.map((family) => {
              const row = heatmap.cell(repository.id, family);
              const status = row?.status ?? "unavailable";
              const title = row
                ? `${status}: ${formatInteger(row.evidenced)} of ${formatInteger(row.total)} evidenced; excluded ${formatInteger(row.excluded)} unavailable ${formatInteger(row.unavailable)}`
                : "unavailable: no persisted cell";
              const label = `${coverageStatusLabel(status)}${row ? ` ${formatInteger(row.evidenced)}/${formatInteger(row.total)}` : ""}`;
              const gapHref =
                row && row.status !== "reviewed"
                  ? `/scans?scanId=${encodeURIComponent(row.scanId)}&family=${encodeURIComponent(family)}`
                  : null;
              return (
                <td key={family}>
                  {gapHref ? (
                    <a
                      href={gapHref}
                      className={`coverage-state coverage-state--${status}`}
                      title={title}
                      onClick={(event) => handleInternalNavigation(event, navigate)}
                    >
                      {label}
                    </a>
                  ) : (
                    <span className={`coverage-state coverage-state--${status}`} title={title}>
                      {label}
                    </span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CoverageReportView({
  report,
  navigate,
  onPreviousPage,
  onNextPage,
  onHeatmapPreviousPage,
  onHeatmapNextPage,
}: {
  report: CoverageReport;
  navigate: (path: string) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onHeatmapPreviousPage?: () => void;
  onHeatmapNextPage?: () => void;
}) {
  const heatmapRows = report.heatmap.length > 0 ? report.heatmap : report.matrix;
  const heatmap = repositoryFamilyHeatmap(heatmapRows, { cartesian: report.heatmap.length > 0 });
  const checklistRepos =
    heatmap.repositories.length > 0 ? heatmap.repositories : [{ id: "none", label: "This scan" }];
  const trustedCount = checklistRepos.reduce((sum, repo) => {
    return (
      sum +
      HEATMAP_FAMILIES.filter((family) => heatmap.cell(repo.id, family)?.status === "reviewed")
        .length
    );
  }, 0);
  const familySlots = Math.max(1, checklistRepos.length) * HEATMAP_FAMILIES.length;
  const gapGroups = groupUnreviewedCells(report.unreviewedCells);

  return (
    <section className="page-section evidence-results" aria-labelledby="coverage-heading">
      <header className="evidence-list-header">
        <div>
          <h2 id="coverage-heading">What can you trust?</h2>
          <p>
            An empty finding list does not mean this coverage is clean. Reviewed is the only
            clean-looking state. Every count names its numerator, denominator, freshness, and
            definition.
          </p>
        </div>
        <p className="trust-checklist__score">
          {trustedCount} of {familySlots} families reviewed
        </p>
      </header>
      {checklistRepos.map((repo) => (
        <ol
          className="trust-checklist"
          aria-label={`Evidence trust checklist for ${repo.label}`}
          key={repo.id}
        >
          {HEATMAP_FAMILIES.map((family) => {
            const row = heatmap.cell(repo.id, family);
            const status = row?.status ?? "unavailable";
            const trust = trustFromStatus(status);
            const href = row ? safeConsoleHref(row.queryHref) : null;
            const body = (
              <>
                <span className={`trust-checklist__mark trust-checklist__mark--${trust.kind}`} />
                <span className="trust-checklist__family">
                  {FAMILY_LABELS[family]}
                  <small>{trust.verdict}</small>
                </span>
                <span className="trust-checklist__why">
                  {trust.why}
                  {row
                    ? ` ${formatInteger(row.evidenced)}/${formatInteger(row.total)} evidenced · ${formatInteger(
                        row.findingCount ||
                          report.matrix.find(
                            (item) =>
                              item.repositoryId === repo.id &&
                              item.family === family &&
                              item.componentKey == null,
                          )?.findingCount ||
                          0,
                      )} findings.`
                    : " No persisted cell."}
                </span>
              </>
            );
            return (
              <li
                className={`trust-checklist__item trust-checklist__item--${trust.kind}`}
                key={family}
              >
                {href ? (
                  <a href={href} onClick={(event) => handleInternalNavigation(event, navigate)}>
                    {body}
                  </a>
                ) : (
                  <div>{body}</div>
                )}
              </li>
            );
          })}
        </ol>
      ))}
      <section className="evidence-count-rail" aria-label="Evidence totals">
        {report.items.map((item) => {
          const ratio = item.total > 0 ? item.covered / item.total : 0;
          const href = safeConsoleHref(item.queryHref);
          const body = (
            <>
              <span className="evidence-count-rail__label">{item.label}</span>
              <strong>{coverageCardValue(item, ratio)}</strong>
              <small>{coverageCardCount(item, ratio)}</small>
              <span className="sr-only">{coverageCardMeaning(item, ratio)}</span>
              <span className="sr-only">
                As of {formatDateTime(item.asOf)} · {item.countDefinitionId}
              </span>
            </>
          );
          return href ? (
            <a
              className="evidence-count-rail__item"
              href={href}
              key={item.id}
              title="Inspect coverage"
              onClick={(event) => handleInternalNavigation(event, navigate)}
            >
              {body}
            </a>
          ) : (
            <div className="evidence-count-rail__item" key={item.id}>
              {body}
            </div>
          );
        })}
      </section>
      {report.unreviewedCells && report.unreviewedCells.length > 0 ? (
        <section className="coverage-gap-files" aria-labelledby="coverage-gaps-heading">
          <h3 id="coverage-gaps-heading">Files that are not reviewed</h3>
          <p>
            These cells are never treated as clean. Open the scan record to see why the file was
            failed, blocked, unsupported, excluded, stale, or unavailable.
          </p>
          <ul className="trust-gap-summary">
            {gapGroups.map((group) => (
              <li key={group.key}>
                <a href={group.href} onClick={(event) => handleInternalNavigation(event, navigate)}>
                  <strong>
                    {formatInteger(group.count)} {group.family.toUpperCase()}
                  </strong>
                  <span>{group.state}</span>
                  <small>{group.reason}</small>
                </a>
              </li>
            ))}
          </ul>
          <details className="evidence-more">
            <summary>
              Show {formatInteger(report.unreviewedCells.length)} individual file rows
            </summary>
            <ul className="evidence-issue-list">
              {report.unreviewedCells.map((cell) => (
                <li key={`${cell.scanId}:${cell.path}:${cell.family}`}>
                  <a
                    className="evidence-issue-row"
                    href={cell.href}
                    onClick={(event) => handleInternalNavigation(event, navigate)}
                  >
                    <span
                      className={`coverage-state coverage-state--${cell.state}`}
                      aria-hidden="true"
                    />
                    <span className="evidence-issue-row__id">{cell.family.toUpperCase()}</span>
                    <span className="evidence-issue-row__title">
                      {cell.repositoryLabel}
                      <small>
                        {cell.path} · {cell.reason}
                      </small>
                    </span>
                    <span className="evidence-issue-row__meta">{cell.state}</span>
                  </a>
                </li>
              ))}
            </ul>
          </details>
          <p>
            Unreviewed files are not a finding queue. After you inspect a gap, open the pending
            findings that still need a decision.
          </p>
          <a
            href={pendingFindingsHref(report.scope.scanId)}
            onClick={(event) => handleInternalNavigation(event, navigate)}
          >
            Open pending finding queue
          </a>
        </section>
      ) : null}
      <details className="evidence-more">
        <summary>Evidence by repository</summary>
        <p>
          Repository and exact observed component identities connect every result to the analysis
          family that produced it. Reviewed is the only clean-looking state and always shows its
          denominator. Failed, blocked, excluded, unavailable, unsupported, stale, and
          mixed-not-reviewed remain distinct.
        </p>
        <CoverageFamilyHeatmap
          matrix={report.heatmap.length > 0 ? report.heatmap : report.matrix}
          cartesian={report.heatmap.length > 0}
          navigate={navigate}
        />
        {report.heatmapTotal > report.heatmapPageSize * 6 ? (
          <nav className="pagination" aria-label="Coverage heatmap pagination">
            <button
              className="button button--small"
              type="button"
              disabled={report.heatmapPage <= 1}
              onClick={onHeatmapPreviousPage}
            >
              Previous
            </button>
            <span>
              Page {report.heatmapPage} of{" "}
              {Math.ceil(report.heatmapTotal / Math.max(6, report.heatmapPageSize * 6))} ·{" "}
              {formatInteger(report.heatmapTotal)} family cells
            </span>
            <button
              className="button button--small"
              type="button"
              disabled={report.heatmapPage * report.heatmapPageSize * 6 >= report.heatmapTotal}
              onClick={onHeatmapNextPage}
            >
              Next
            </button>
          </nav>
        ) : null}
        <div className="table-scroll">
          <table
            className="data-table coverage-matrix-table evidence-issue-table"
            aria-label="Estate coverage matrix"
          >
            <thead>
              <tr>
                <th>Repository / component</th>
                <th>Family</th>
                <th>Engines</th>
                <th>State</th>
                <th>Evidence</th>
                <th>Canonical findings</th>
                <th>Freshness</th>
              </tr>
            </thead>
            <tbody>
              {report.matrix.map((row) => {
                const href = safeConsoleHref(row.queryHref);
                return (
                  <tr className="evidence-issue-table__row" key={row.id}>
                    <td>
                      <strong>{row.repositoryLabel}</strong>
                      <small className="cell-secondary">
                        {row.componentLabel ?? "Repository total"}
                      </small>
                    </td>
                    <td className="evidence-issue-row__id">{row.family.toUpperCase()}</td>
                    <td title={row.engineIds.join(", ")}>
                      {formatInteger(row.engineIds.length)} persisted
                    </td>
                    <td>
                      <span className={`coverage-state coverage-state--${row.status}`}>
                        {coverageStatusLabel(row.status)}
                      </span>
                    </td>
                    <td>
                      {formatInteger(row.evidenced)} / {formatInteger(row.total)} evidenced
                      {row.gaps > 0 ? (
                        <small className="cell-secondary">{row.gaps} gaps</small>
                      ) : null}
                    </td>
                    <td>
                      {href ? (
                        <a
                          href={href}
                          onClick={(event) => handleInternalNavigation(event, navigate)}
                        >
                          {formatInteger(row.findingCount)} findings
                        </a>
                      ) : (
                        formatInteger(row.findingCount)
                      )}
                    </td>
                    <td className="evidence-issue-row__meta">{formatDateTime(row.asOf)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {report.matrixTotal > report.matrixPageSize ? (
          <nav className="pagination" aria-label="Coverage matrix pagination">
            <button
              className="button button--small"
              type="button"
              disabled={report.matrixPage <= 1}
              onClick={onPreviousPage}
            >
              Previous
            </button>
            <span>
              Page {report.matrixPage} of {Math.ceil(report.matrixTotal / report.matrixPageSize)} ·{" "}
              {formatInteger(report.matrixTotal)} rows
            </span>
            <button
              className="button button--small"
              type="button"
              disabled={report.matrixPage * report.matrixPageSize >= report.matrixTotal}
              onClick={onNextPage}
            >
              Next
            </button>
          </nav>
        ) : null}
        {report.matrix.length === 0 ? (
          <p className="unavailable-value">
            Not started — no selected/latest scan coverage cells exist.
          </p>
        ) : null}
      </details>
      <details className="limitations coverage-limitations" role="note">
        <summary>What can limit this coverage view?</summary>
        <ul>
          {report.limitations.map((limitation) => (
            <li key={limitation}>{limitation}</li>
          ))}
        </ul>
      </details>
    </section>
  );
}
